import type { CreateUserForm } from '@/features/team/types/team.types';
import { InfoTooltip, type Column, type MobileConfig } from '@chikara/ui';
import { createElement, Fragment } from 'react';
import { t } from '@/i18n';

/** Encabezado con ícono de ayuda (?) y su tooltip (este archivo es .ts, sin JSX). */
const withTooltip = (text: string, tooltip: string) =>
  createElement(
    Fragment,
    null,
    text,
    ' ',
    createElement(InfoTooltip, { content: tooltip, label: text }),
  );

export const ROLES = {
  ADMIN: 'Admin',
  BASIC: 'Básico'
};

/** Valor centinela para "Todos/Todas" en filtros y pestañas. */
export const TEAM_ALL_VALUE = 'all';

/**
 * Estados de cuenta que se exponen como pestaña y filtro (valores del enum del BE
 * `user-list`). Orden según Figma. Fuente actual: se sustituye por el catálogo del
 * BE cuando aterrice BF-619 (seam).
 */
export const TEAM_STATUS_FILTERS = [
  { value: 'pending_approval', labelKey: 'team.tabs.pending', optionKey: 'team.filters.status.pending' },
  { value: 'enabled', labelKey: 'team.tabs.active', optionKey: 'team.filters.status.active' },
  { value: 'disabled', labelKey: 'team.tabs.inactive', optionKey: 'team.filters.status.inactive' },
  { value: 'rejected', labelKey: 'team.tabs.rejected', optionKey: 'team.filters.status.rejected' },
] as const;

/** Roles para el filtro de Rol (seam BF-619). */
export const TEAM_ROLE_FILTERS = [
  { value: 'OWNER', labelKey: 'team.filters.role.owner' },
  { value: 'ADMIN', labelKey: 'team.filters.role.admin' },
  { value: 'BASIC', labelKey: 'team.filters.role.basic' },
] as const;


/** Etiquetas de estatus para filtros y encabezado de export (incluye los del catálogo BE). */
export const TEAM_STATUS_LABELS: Record<string, string> = {
  pending_approval: t('team.filters.status.pending'),
  enabled: t('team.filters.status.active'),
  disabled: t('team.filters.status.inactive'),
  rejected: t('team.filters.status.rejected'),
  in_progress: 'En progreso',
  invited: 'Pendiente de aceptación',
  blocked: 'Bloqueado',
};

export function teamStatusLabel(value: string): string {
  return TEAM_STATUS_LABELS[value] ?? value;
}

export const TeamMemberStatus = {
  enabled: 'Activo',
  blocked: 'Bloqueado',
  disabled: 'Inactivo',
  pending_approval: 'Pendiente',
  rejected: 'Rechazado',
  invited: 'Pendiente de aceptación',
  in_progress: 'En progreso'
};


export const TEAM_COLUMNS: Column[] = [
  { key: 'email', label: t('team.table.email'), width: '14%' },
  { key: 'fullname', label: t('team.table.name'), width: '16%' },
  { key: 'monthly_limit', label: withTooltip(t('team.table.spendingLimit'), t('tooltips.teamLimit')), width: '14%', align: 'right' },
  { key: 'disposable', label: withTooltip(t('team.table.disposable'), t('tooltips.teamAvailable')), width: '12%', align: 'right' },
  { key: 'role', label: withTooltip(t('team.table.role'), t('tooltips.teamType')), width: '10%' },
  { key: 'status', label: withTooltip(t('team.table.status'), t('tooltips.teamStatus')), width: '12%' },
  { key: 'action', label: '', width: '10%' },
];

export const TEAM_MOBILE_CONFIG: MobileConfig = {
  primaryField: 'email',
  secondaryField: 'fullname',
  expandableFields: ['disposable', 'monthly_limit', 'role', 'status', 'action'],
};

export const defaultUserFormValues: CreateUserForm = {
  role: 'BASIC' as const,
  email: '',
  name: '',
  lname: '',
  slname: '',
  monthly_limit: '',
  phone: '',
  origin: { source: 'invitation' },
};
